// [S-061d] Activity feed — full live feed of every action across both
// sources. Direct port of docs/Wireframes/Activity.html. Filters: time
// range, source (cert / stud / both), tool, actor. Day-grouped rows
// with Master-Rule-1.6-compliant edge attribution (stacked, never
// blended). Reads /tenants/me/activity via useActivityFeed().
//
// Cursor pagination: backend returns nextCursor; "Load more" appends
// the next page client-side. Filters apply client-side after the fetch
// (server can ingest them later — see follow-up TODO in queries.ts).
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  PageHead,
  Pagination,
  Select,
  usePagination,
} from "../components";
import {
  useActivityFeed,
  type ActivityFeedFilter,
  type ActivityRow,
  type ActivitySourceFilter,
} from "../data/queries";

const SOURCE_OPTIONS: ReadonlyArray<{
  value: ActivitySourceFilter;
  label: string;
}> = [
  { value: "all", label: "All sources" },
  { value: "L1", label: "Certified only" },
  { value: "L2", label: "Precedent only" },
  { value: "both", label: "Both sources" },
];

const RANGE_OPTIONS: ReadonlyArray<{
  value: number; // days
  label: string;
}> = [
  { value: 1, label: "Last 24h" },
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
];

export function Activity(): JSX.Element {
  const [rangeDays, setRangeDays] = useState<number>(7);
  const [source, setSource] = useState<ActivitySourceFilter>("all");
  const [actor, setActor] = useState<string>("");
  const [pages, setPages] = useState<ActivityRow[][]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const navigate = useNavigate();

  const filter = useMemo<ActivityFeedFilter>(() => {
    const since = new Date(
      Date.now() - rangeDays * 24 * 60 * 60 * 1000
    ).toISOString();
    return {
      source,
      since,
      ...(actor.trim().length > 0 ? { actor: actor.trim() } : {}),
    };
  }, [rangeDays, source, actor]);

  const { data, isLoading, isError, isFetching, refetch } = useActivityFeed(
    filter,
    cursor
  );

  // Aggregate paginated pages with the freshest page replacing the
  // currently-fetched slot. Resetting filter clears `pages` and
  // `cursor`, so the visible rows always reflect the active filter.
  const allRows = useMemo<ActivityRow[]>(() => {
    const fetchedRows = data?.rows ?? [];
    return [...pages.flat(), ...fetchedRows];
  }, [pages, data?.rows]);

  // Page the loaded events (numbered, uniform), grouping by day WITHIN the page.
  // "Load more" still extends the loaded pool from the server cursor.
  const pager = usePagination(allRows, 30);
  const grouped = useMemo(() => groupByDay(pager.pageItems), [pager.pageItems]);

  function resetAndApply(next: () => void): void {
    setPages([]);
    setCursor(null);
    next();
  }

  function handleLoadMore(): void {
    if (data === undefined || data.nextCursor === null) return;
    setPages((prev) => [...prev, data.rows]);
    setCursor(data.nextCursor);
  }

  return (
    <>
      <PageHead
        eyebrow="Operations · activity"
        title={
          <>
            Every <em>question</em>, every answer.
          </>
        }
        sub="A running log of every action across both sources. Filter, deep-link to source."
        mockup="The activity log itself is live. Export CSV (coming soon, S-243) and Verify integrity (coming soon, S-093) aren't wired up yet."
        cta={
          <>
            <Button
              variant="secondary"
              className="btn--s"
              disabled
              title="Export CSV — coming soon (S-243)"
            >
              Export CSV
            </Button>
            <Button
              variant="primary"
              className="btn--s"
              disabled
              title="Verify integrity — coming soon (S-093)"
            >
              Verify integrity ✓
            </Button>
          </>
        }
      />

      <div className="tpl__filters" role="group" aria-label="Activity filters">
        <span className="tpl__filterlbl">Filter</span>
        <RangeFilter
          value={rangeDays}
          onChange={(v) => resetAndApply(() => setRangeDays(v))}
        />
        <SourceFilter
          value={source}
          onChange={(v) => resetAndApply(() => setSource(v))}
        />
        <ActorFilter
          value={actor}
          onChange={(v) => resetAndApply(() => setActor(v))}
        />
        <span className="tpl__count">
          {isLoading
            ? "loading…"
            : `${allRows.length} event${allRows.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {isError ? (
        <Alert
          label="Couldn't load the activity feed"
          tone="error"
          cta={
            <Button
              variant="bare"
              onClick={() => {
                void refetch();
              }}
            >
              Retry
            </Button>
          }
        >
          Try again in a moment.
        </Alert>
      ) : (
        <div
          className="tbl tbl--audit tbl--act"
          role="table"
          aria-label="Activity feed"
        >
          <div className="tbl__row tbl__row--head" role="row">
            <span role="columnheader">Time</span>
            <span role="columnheader">Action</span>
            <span role="columnheader">Actor</span>
            <span role="columnheader">Source</span>
          </div>

          {grouped.length === 0 && !isLoading && !isFetching ? (
            <div className="act__empty">
              No events match the current filter.
            </div>
          ) : (
            grouped.map((group) => (
              <DayGroup key={group.dayKey} group={group} onRowClick={navigateForRow(navigate)} />
            ))
          )}
        </div>
      )}

      {!isError && (
        <Pagination
          page={pager.page}
          pageCount={pager.pageCount}
          onPageChange={pager.setPage}
          total={pager.total}
          rangeStart={pager.rangeStart}
          rangeEnd={pager.rangeEnd}
          itemLabel="event"
        />
      )}

      <div className="act__more">
        {data?.nextCursor != null ? (
          <Button
            variant="secondary"
            className="btn--s"
            onClick={handleLoadMore}
            loading={isFetching}
          >
            Load more
          </Button>
        ) : (
          allRows.length > 0 && (
            <span className="tpl__filterlbl">— end of feed —</span>
          )
        )}
      </div>
    </>
  );
}

type DayGroupShape = {
  dayKey: string;
  label: string;
  rows: ActivityRow[];
};

function DayGroup({
  group,
  onRowClick,
}: {
  group: DayGroupShape;
  onRowClick: (row: ActivityRow) => void;
}): JSX.Element {
  return (
    <>
      <div className="act__day" role="row">
        {group.label}
      </div>
      {group.rows.map((row) => (
        <ActivityRowItem key={row.id} row={row} onClick={onRowClick} />
      ))}
    </>
  );
}

function ActivityRowItem({
  row,
  onClick,
}: {
  row: ActivityRow;
  onClick: (row: ActivityRow) => void;
}): JSX.Element {
  const time = new Date(row.occurredAt).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return (
    <div
      className="tbl__row"
      role="link"
      tabIndex={0}
      onClick={() => onClick(row)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(row);
        }
      }}
    >
      <span className="tbl__time">{time}</span>
      <div className="tbl__what">
        <SourceEdge source={row.layerSource} />
        <div>
          <b>{row.title}</b>
          <em>{row.meta}</em>
        </div>
      </div>
      <span className="tbl__who">{row.actor.uid}</span>
      <SourceCell source={row.layerSource} />
    </div>
  );
}

function SourceEdge({
  source,
}: {
  source: ActivityRow["layerSource"];
}): JSX.Element {
  if (source === "both") {
    // Master Rule 1.6: stacked edges, never a blended gradient.
    return (
      <span className="act__edges" aria-label="Both sources">
        <span className="src-edge src-edge--cert" aria-hidden="true" />
        <span className="src-edge src-edge--stud" aria-hidden="true" />
      </span>
    );
  }
  const variant = source === "L1" ? "cert" : "stud";
  return (
    <span
      className={`src-edge src-edge--${variant}`}
      aria-hidden="true"
    />
  );
}

function SourceCell({
  source,
}: {
  source: ActivityRow["layerSource"];
}): JSX.Element {
  if (source === "both") {
    return (
      <span className="act__bothcol">
        <span className="dots">
          <span className="dot dot--cert" />
          <span className="dot dot--stud" />
        </span>
        both
      </span>
    );
  }
  if (source === "L1") {
    return (
      <span className="tbl__src">
        <span className="dot dot--cert" />
        Certified
      </span>
    );
  }
  return (
    <span className="tbl__src">
      <span className="dot dot--stud" />
      Precedent
    </span>
  );
}

// Filter primitives — wrap the design-system <Select> (S-061f) so the
// chip styling is preserved while keyboard a11y is owned by Radix.
function RangeFilter({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}): JSX.Element {
  return (
    <Select
      label="Time range"
      value={String(value)}
      options={RANGE_OPTIONS.map((o) => ({
        value: String(o.value),
        label: o.label,
      }))}
      onValueChange={(v) => {
        if (v !== undefined) onChange(Number(v));
      }}
      clearable={false}
      triggerClassName="tpl__filter is-on"
    />
  );
}

function SourceFilter({
  value,
  onChange,
}: {
  value: ActivitySourceFilter;
  onChange: (v: ActivitySourceFilter) => void;
}): JSX.Element {
  const isOn = value !== "all";
  return (
    <Select
      label="Source filter"
      value={value}
      options={SOURCE_OPTIONS}
      onValueChange={(v) => onChange(v ?? "all")}
      clearable={false}
      triggerClassName={`tpl__filter${isOn ? " is-on" : ""}`}
    />
  );
}

function ActorFilter({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <label className={`tpl__filter${value.length > 0 ? " is-on" : ""}`}>
      <span aria-hidden="true">
        {value.length > 0 ? `Actor: ${value}` : "All actors ⌄"}
      </span>
      <input
        type="text"
        className="tpl__filter__select"
        value={value}
        placeholder="filter by uid"
        onChange={(e) => onChange(e.target.value)}
        aria-label="Actor uid filter"
      />
    </label>
  );
}

function navigateForRow(
  navigate: ReturnType<typeof useNavigate>
): (row: ActivityRow) => void {
  return (row) => {
    // Best-effort deep links until tool-specific result IDs land on the
    // ActivityRow type. The toolName carries enough to hop to the
    // right surface; specific entity IDs become possible when the
    // backend includes them on the row.
    if (row.toolName.includes("re_attest") || row.title.includes("Re-attest")) {
      navigate("/reattestation");
      return;
    }
    if (row.toolName.includes("template") || row.title.includes("TMPL-")) {
      // Land on the library list — list is the safe default until the
      // row carries a templateId.
      navigate("/library");
      return;
    }
    if (row.toolName.includes("precedent") || row.title.includes("precedent")) {
      navigate("/precedent");
      return;
    }
    if (row.toolName.includes("review") || row.title.includes("Reviewed")) {
      navigate("/drafts");
      return;
    }
    if (row.toolName.includes("brief") || row.title.includes("brief")) {
      navigate("/briefs");
    }
  };
}

function groupByDay(rows: ActivityRow[]): DayGroupShape[] {
  const today = startOfDay(new Date());
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const groups = new Map<string, DayGroupShape>();
  for (const row of rows) {
    const d = startOfDay(new Date(row.occurredAt));
    const key = d.toISOString().slice(0, 10);
    let label: string;
    if (d.getTime() === today.getTime()) {
      label = `Today · ${formatDay(d)}`;
    } else if (d.getTime() === yesterday.getTime()) {
      label = `Yesterday · ${formatDay(d)}`;
    } else {
      label = formatDay(d);
    }
    let group = groups.get(key);
    if (group === undefined) {
      group = { dayKey: key, label, rows: [] };
      groups.set(key, group);
    }
    group.rows.push(row);
  }
  // Sort groups newest-first; rows within already arrive newest-first
  // from the API.
  return Array.from(groups.values()).sort((a, b) =>
    b.dayKey.localeCompare(a.dayKey)
  );
}

function startOfDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function formatDay(d: Date): string {
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
